import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import PortalLayout from "@/components/PortalLayout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "@/hooks/use-toast";
import { Lock, Globe, Bell, Shield, User } from "lucide-react";

const TIMEZONES = [
  "UTC", "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
  "America/Anchorage", "America/Sao_Paulo", "America/Argentina/Buenos_Aires",
  "Europe/London", "Europe/Paris", "Europe/Berlin", "Europe/Moscow",
  "Africa/Lagos", "Africa/Johannesburg", "Africa/Cairo",
  "Asia/Dubai", "Asia/Kolkata", "Asia/Singapore", "Asia/Tokyo", "Asia/Shanghai",
  "Australia/Sydney", "Pacific/Auckland",
];

const PortalAccountSettings = () => {
  const { user, fullName, companyName } = useAuth();

  // Profile
  const [displayName, setDisplayName] = useState(fullName || "");
  const [saving, setSaving] = useState(false);

  // Password
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordLoading, setPasswordLoading] = useState(false);

  // Preferences
  const [timezone, setTimezone] = useState(
    () => localStorage.getItem("user_timezone") || Intl.DateTimeFormat().resolvedOptions().timeZone
  );
  const [emailNotifications, setEmailNotifications] = useState(true);

  const handleSaveProfile = async () => {
    if (!user) return;
    setSaving(true);
    await supabase.from("profiles").update({ full_name: displayName }).eq("user_id", user.id);
    await supabase.auth.updateUser({ data: { full_name: displayName } });
    toast({ title: "Profile saved" });
    setSaving(false);
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword.length < 8) {
      toast({ title: "Password too short", description: "Minimum 8 characters", variant: "destructive" });
      return;
    }
    if (newPassword !== confirmPassword) {
      toast({ title: "Passwords don't match", variant: "destructive" });
      return;
    }
    setPasswordLoading(true);
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Password updated successfully" });
      setNewPassword("");
      setConfirmPassword("");
    }
    setPasswordLoading(false);
  };

  const handleSavePreferences = () => {
    localStorage.setItem("user_timezone", timezone);
    toast({ title: "Preferences saved" });
  };

  return (
    <PortalLayout>
      <div className="max-w-3xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-display font-bold text-primary-foreground">Account Settings</h1>
          <p className="text-sm text-primary-foreground/40 font-body mt-1">Manage your profile and security.</p>
        </div>

        {/* Profile Card */}
        <Card className="bg-primary-foreground/5 border-primary-foreground/10">
          <CardHeader>
            <CardTitle className="text-lg text-primary-foreground flex items-center gap-2"><User className="w-4 h-4" /> Profile</CardTitle>
            <CardDescription className="text-primary-foreground/40">Update your display name.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label className="text-primary-foreground/60">Full Name</Label>
                <Input value={displayName} onChange={e => setDisplayName(e.target.value)} placeholder="Your name" className="bg-primary-foreground/5 border-primary-foreground/10 text-primary-foreground" />
              </div>
              <div>
                <Label className="text-primary-foreground/60">Company</Label>
                <Input value={companyName || ""} disabled className="bg-primary-foreground/5 border-primary-foreground/10 text-primary-foreground/50" />
              </div>
            </div>
            <div>
              <Label className="text-primary-foreground/60">Email</Label>
              <Input value={user?.email || ""} disabled className="bg-primary-foreground/5 border-primary-foreground/10 text-primary-foreground/50" />
            </div>
            <Button onClick={handleSaveProfile} disabled={saving} className="w-full sm:w-auto">
              {saving ? "Saving…" : "Save Profile"}
            </Button>
          </CardContent>
        </Card>

        {/* Security Card */}
        <Card className="bg-primary-foreground/5 border-primary-foreground/10">
          <CardHeader>
            <CardTitle className="text-lg text-primary-foreground flex items-center gap-2"><Lock className="w-4 h-4" /> Change Password</CardTitle>
            <CardDescription className="text-primary-foreground/40">Use a strong password with at least 8 characters.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <Label className="text-primary-foreground/60">New Password</Label>
                <Input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Minimum 8 characters" required className="bg-primary-foreground/5 border-primary-foreground/10 text-primary-foreground" />
              </div>
              <div>
                <Label className="text-primary-foreground/60">Confirm Password</Label>
                <Input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Re-enter your new password" required className="bg-primary-foreground/5 border-primary-foreground/10 text-primary-foreground" />
              </div>
              <Button type="submit" disabled={passwordLoading} variant="destructive" className="w-full sm:w-auto">
                {passwordLoading ? "Updating…" : "Update Password"}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Account Info */}
        <Card className="bg-primary-foreground/5 border-primary-foreground/10">
          <CardHeader>
            <CardTitle className="text-lg text-primary-foreground flex items-center gap-2"><Shield className="w-4 h-4" /> Account Info</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-primary-foreground/40">Account Created</span>
              <span className="text-primary-foreground font-medium">{user?.created_at ? new Date(user.created_at).toLocaleDateString() : "—"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-primary-foreground/40">Last Sign In</span>
              <span className="text-primary-foreground font-medium">{user?.last_sign_in_at ? new Date(user.last_sign_in_at).toLocaleDateString() : "—"}</span>
            </div>
          </CardContent>
        </Card>

        {/* Preferences */}
        <Card className="bg-primary-foreground/5 border-primary-foreground/10">
          <CardHeader>
            <CardTitle className="text-lg text-primary-foreground flex items-center gap-2"><Globe className="w-4 h-4" /> Preferences</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-primary-foreground/60">Time Zone</Label>
              <Select value={timezone} onValueChange={setTimezone}>
                <SelectTrigger className="w-full sm:w-80 bg-primary-foreground/5 border-primary-foreground/10 text-primary-foreground">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIMEZONES.map(tz => (
                    <SelectItem key={tz} value={tz}>{tz.replace(/_/g, " ")}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-primary-foreground">Email Notifications</p>
                <p className="text-xs text-primary-foreground/40">Receive email alerts for document and project updates</p>
              </div>
              <Switch checked={emailNotifications} onCheckedChange={setEmailNotifications} />
            </div>
            <Button onClick={handleSavePreferences} className="w-full sm:w-auto">Save Preferences</Button>
          </CardContent>
        </Card>
      </div>
    </PortalLayout>
  );
};

export default PortalAccountSettings;
